import React from 'react';
import { motion } from 'framer-motion';
import { usePageTransition, TextReveal, ArchFramedImage } from './PageTransition';

// Scandinavian Color Palette
const COLORS = {
  primary: '#2F2F2F',      // Deep charcoal blue
  secondary: '#6B705C',    // Dusty blue-grey
  accent: '#B8A398',       // Warm taupe/sand
  gold: '#C97A2B',         // Muted gold
  light: '#F5F3EF',        // Off-white/cream
  dark: '#1A1A1A',         // Near black
};

// Unified page layout wrapper for all inner pages
export const GucciPageLayout = ({ children }) => {
  return (
    <div className="bg-[#F5F3EF] min-h-screen pt-[70px]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Manrope:wght@300;400;500&display=swap');
      `}</style>
      {children}
    </div>
  );
};

// Page hero section with arch image
export const PageHero = ({ title, subtitle, image, description }) => {
  return (
    <section className="relative py-16 md:py-24 bg-[#F5F3EF]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header text */}
        <div className="text-center mb-12">
          <motion.p 
            className="text-[#6B705C] text-xs tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {subtitle}
          </motion.p>
          <motion.h1 
            className="text-4xl md:text-6xl lg:text-7xl text-[#2F2F2F]"
            style={{ fontFamily: 'DM Serif Display, serif' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            {title}
          </motion.h1>
          {description && (
            <motion.p 
              className="text-[#6B705C] text-base md:text-lg font-light mt-6 max-w-xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {description}
            </motion.p>
          )}
        </div>

        {/* Arch framed hero image */}
        {image && (
          <div className="max-w-3xl mx-auto">
            <ArchFramedImage 
              src={image}
              alt={title}
              className="h-[400px] md:h-[500px]"
            />
          </div>
        )}
      </div>
    </section>
  );
};

// Section with light background
export const LightSection = ({ children, className = '' }) => {
  return (
    <section className={`py-16 md:py-24 bg-[#F5F3EF] ${className}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {children}
      </div>
    </section>
  );
};

// Section with dark background
export const DarkSection = ({ children, className = '' }) => {
  return (
    <section className={`py-16 md:py-24 bg-[#1A1A1A] ${className}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {children}
      </div>
    </section>
  );
};

// Section with primary color background (Scandinavian blue)
export const BurgundySection = ({ children, className = '' }) => {
  return (
    <section className={`py-16 md:py-24 bg-[#2F2F2F] ${className}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {children}
      </div>
    </section>
  );
};

// Section header
export const SectionHeader = ({ 
  subtitle, 
  title, 
  description, 
  light = true,
  centered = true 
}) => {
  const textColor = light ? '#2F2F2F' : 'white';
  const subtitleColor = light ? '#6B705C' : '#C97A2B';
  const descColor = light ? '#6B705C' : 'rgba(255,255,255,0.6)';

  return (
    <div className={`mb-12 md:mb-16 ${centered ? 'text-center' : ''}`}>
      <TextReveal>
        <p 
          className="text-xs tracking-[0.3em] uppercase mb-4"
          style={{ color: subtitleColor }}
        >
          {subtitle}
        </p>
      </TextReveal>
      <TextReveal delay={0.1}>
        <h2 
          className="text-3xl md:text-4xl lg:text-5xl mb-4"
          style={{ fontFamily: 'DM Serif Display, serif', color: textColor }}
        >
          {title}
        </h2>
      </TextReveal>
      {description && (
        <TextReveal delay={0.2}>
          <p 
            className={`font-light text-base md:text-lg ${centered ? 'max-w-2xl mx-auto' : ''}`}
            style={{ color: descColor }}
          >
            {description}
          </p>
        </TextReveal>
      )}
    </div>
  );
};

// CTA Button (gold style)
export const GoldButton = ({ children, onClick, className = '' }) => {
  return (
    <motion.button
      onClick={onClick}
      className={`bg-[#C97A2B] text-black px-8 py-4 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-[#2F2F2F] hover:text-white transition-colors duration-500 ${className}`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {children}
    </motion.button>
  );
};

// CTA Button (outline style)
export const OutlineButton = ({ children, onClick, light = true, className = '' }) => {
  const borderColor = light ? '#2F2F2F' : '#C97A2B';
  const textColor = light ? '#2F2F2F' : '#C97A2B';
  
  return (
    <motion.button
      onClick={onClick}
      className={`border px-8 py-4 text-xs tracking-[0.15em] uppercase transition-all duration-500 ${className}`}
      style={{ 
        borderColor, 
        color: textColor,
      }}
      whileHover={{ 
        scale: 1.02,
        backgroundColor: borderColor,
        color: light ? 'white' : 'black'
      }}
      whileTap={{ scale: 0.98 }}
    >
      {children}
    </motion.button>
  );
};

// Unified Footer for all pages
export const GucciFooter = () => {
  const { navigateWithTransition } = usePageTransition();
  
  return (
    <footer className="bg-[#1A1A1A] py-16 md:py-20">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <h3 
              className="text-3xl text-[#C97A2B] mb-4 italic"
              style={{ fontFamily: 'DM Serif Display, serif' }}
            >
              Sunsari
            </h3>
            <p className="text-white/50 font-light leading-relaxed max-w-md text-sm">
              An exquisite journey through the flavors of India & Nepal, 
              bringing authentic cuisine to the heart of Vaasa, Finland.
            </p>
          </div>

          <div>
            <p className="text-white text-xs tracking-[0.2em] uppercase mb-4">Contact</p>
            <div className="space-y-2 text-white/50 font-light text-sm">
              <p>Hietasaarenkatu 18</p>
              <p>65100 Vaasa, Finland</p>
              <p className="text-[#C97A2B]">+358 40 377 9200</p>
              <p>ravintolasunsari@gmail.com</p>
            </div>
          </div>

          <div>
            <p className="text-white text-xs tracking-[0.2em] uppercase mb-4">Hours</p>
            <div className="space-y-2 text-white/50 font-light text-sm">
              <p>Mon - Thu: 11:00 - 21:00</p>
              <p>Friday: 11:00 - 22:00</p>
              <p>Saturday: 12:00 - 22:00</p>
              <p>Sunday: 12:00 - 20:00</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-white/10">
          <p className="text-white/30 text-xs">© 2025 Sunsari Restaurant</p>
          <div className="flex gap-6 mt-4 md:mt-0">
            {[
              { name: 'Home', path: '/' },
              { name: 'Menu', path: '/menu' },
              { name: 'About', path: '/about' },
              { name: 'Contact', path: '/contact' }
            ].map((item) => (
              <button
                key={item.name}
                onClick={() => navigateWithTransition(item.path)}
                className="text-white/50 text-xs tracking-[0.1em] uppercase hover:text-[#C97A2B] transition-colors"
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default GucciPageLayout;
